import React, { useEffect, useMemo, useState } from 'react';
import {
  PERSON_OPTIONS,
  SERVING_UNITS,
  calcTotalQuantity,
  calcLineTotal,
  formatQtyWithUnit,
  formatAmount
} from '../utils/orderFoodUtils';
import { clearTsrCustomerToken } from '../auth/tsrCustomerAuth';
import VerifyIdentityStep from './VerifyIdentityStep';
import PlaceOrderStep from './PlaceOrderStep';

const EllipsisText = ({ text, className = '', style = {} }) => {
  const value = text || '';
  return (
    <div
      className={className}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
        ...style
      }}
      title={value}
    >
      {value}
    </div>
  );
};

const STEP_TITLES = {
  customize: 'Customize package for this order',
  'verify-identity': 'Verify your identity',
  'place-order': 'Review your order'
};

const CustomizePackageModal = ({ packageState, loading, initialCouponCode, onClose }) => {
  const [step, setStep] = useState('customize');
  const [persons, setPersons] = useState(10);
  const [items, setItems] = useState([]);
  const [verifiedChannel, setVerifiedChannel] = useState('');
  const [verifiedIdentifier, setVerifiedIdentifier] = useState('');
  const [verifiedProfile, setVerifiedProfile] = useState(null);

  useEffect(() => {
    if (!packageState) {
      return;
    }
    setStep('customize');
    setPersons(packageState.persons || 10);
    setItems(packageState.items || []);
    setVerifiedChannel('');
    setVerifiedIdentifier('');
    setVerifiedProfile(null);
    clearTsrCustomerToken();
  }, [packageState]);

  const packageTotal = useMemo(() => {
    return items.reduce((sum, item) => sum + calcLineTotal(item, persons), 0);
  }, [items, persons]);

  const updateItem = (foodItemId, changes) => {
    setItems((prev) => prev.map((item) => {
      if (item.foodItemId !== foodItemId) {
        return item;
      }
      return { ...item, ...changes };
    }));
  };

  const updateQtyPerPerson = (foodItemId, delta) => {
    setItems((prev) => prev
      .map((item) => {
        if (item.foodItemId !== foodItemId) {
          return item;
        }
        const nextQty = Math.max(0, (Number(item.qtyPerPerson) || 0) + delta);
        return { ...item, qtyPerPerson: nextQty };
      })
      .filter((item) => (Number(item.qtyPerPerson) || 0) > 0));
  };

  const handleClose = () => {
    clearTsrCustomerToken();
    onClose();
  };

  const handlePlaceOrder = () => {
    if (!packageState || items.length === 0) {
      window.alert('Add at least one item to place an order.');
      return;
    }
    setStep('verify-identity');
  };

  const handleVerified = ({ channel, identifier, profile }) => {
    setVerifiedChannel(channel);
    setVerifiedIdentifier(identifier);
    setVerifiedProfile(profile);
    setStep('place-order');
  };

  if (!packageState) {
    return null;
  }

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div style={{ minWidth: 0 }}>
              <h5 className="modal-title mb-1">{packageState.packageName}</h5>
              <small className="text-muted">{STEP_TITLES[step]}</small>
            </div>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close" />
          </div>

          {step === 'customize' ? (
            <>
              <div className="modal-body">
                {loading ? (
                  <div className="text-center py-4">
                    <span className="spinner-border spinner-border-sm" role="status" />
                    <span className="ms-2">Loading package details...</span>
                  </div>
                ) : (
                  <>
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <label className="form-label">Persons</label>
                        <select
                          className="form-select"
                          value={persons}
                          onChange={(event) => setPersons(Number(event.target.value))}
                        >
                          {PERSON_OPTIONS.map((value) => (
                            <option key={value} value={value}>{value}</option>
                          ))}
                        </select>
                      </div>
                      {packageState.notes ? (
                        <div className="col-md-8">
                          <label className="form-label">Notes</label>
                          <input type="text" className="form-control" value={packageState.notes} readOnly />
                        </div>
                      ) : null}
                    </div>

                    {items.length === 0 ? (
                      <div className="text-muted">No items in this package.</div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {items.map((item) => {
                          const totalQty = calcTotalQuantity(item, persons);
                          const lineTotal = calcLineTotal(item, persons);
                          const unit = item.servingUnit || 'pcs';

                          return (
                            <div key={item.foodItemId} className="border rounded-3 p-2 bg-light" style={{ minWidth: 0 }}>
                              <div className="d-flex justify-content-between align-items-start gap-2">
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <EllipsisText text={item.name} className="fw-semibold" style={{ fontSize: '0.9rem' }} />
                                  <small className="text-muted d-block">{item.category}</small>
                                  <small className="text-muted d-block">{formatAmount(item.rate)} per {unit}</small>
                                  <small className="text-primary d-block">
                                    {item.scaleByPersons
                                      ? `${formatQtyWithUnit(item.qtyPerPerson, unit)}/person → ${formatQtyWithUnit(totalQty, unit)}`
                                      : `${formatQtyWithUnit(item.qtyPerPerson, unit)} (fixed)`}
                                  </small>
                                </div>
                                <div className="fw-semibold text-end flex-shrink-0" style={{ fontSize: '0.9rem' }}>
                                  {formatAmount(lineTotal)}
                                </div>
                              </div>

                              <div className="row g-2 mt-2 align-items-end">
                                <div className="col-4">
                                  <label className="form-label mb-1" style={{ fontSize: '0.75rem' }}>Qty / person</label>
                                  <div className="input-group input-group-sm">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      onClick={() => updateQtyPerPerson(item.foodItemId, -1)}
                                    >
                                      -
                                    </button>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      className="form-control text-center"
                                      value={item.qtyPerPerson}
                                      onChange={(event) => {
                                        const value = Number(event.target.value);
                                        if (value > 0) {
                                          updateItem(item.foodItemId, { qtyPerPerson: value });
                                        }
                                      }}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      onClick={() => updateQtyPerPerson(item.foodItemId, 1)}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <label className="form-label mb-1" style={{ fontSize: '0.75rem' }}>Unit</label>
                                  <select
                                    className="form-select form-select-sm"
                                    value={item.servingUnit || 'pcs'}
                                    onChange={(event) => updateItem(item.foodItemId, { servingUnit: event.target.value })}
                                  >
                                    {SERVING_UNITS.map((unitOption) => (
                                      <option key={unitOption} value={unitOption}>{unitOption}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="col-4">
                                  <div className="form-check mb-1">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`scale-${item.foodItemId}`}
                                      checked={item.scaleByPersons !== false}
                                      onChange={(event) => updateItem(item.foodItemId, { scaleByPersons: event.target.checked })}
                                    />
                                    <label className="form-check-label" htmlFor={`scale-${item.foodItemId}`} style={{ fontSize: '0.75rem' }}>
                                      Scale by persons
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer justify-content-between">
                <div className="fw-semibold text-primary">{formatAmount(packageTotal)}</div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-light" onClick={handleClose}>Close</button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePlaceOrder}
                    disabled={loading || items.length === 0}
                  >
                    Place Order
                  </button>
                </div>
              </div>
            </>
          ) : null}

          {step === 'verify-identity' ? (
            <VerifyIdentityStep
              onBack={() => setStep('customize')}
              onVerified={handleVerified}
            />
          ) : null}

          {step === 'place-order' ? (
            <PlaceOrderStep
              packageState={packageState}
              persons={persons}
              items={items}
              packageTotal={packageTotal}
              verifiedChannel={verifiedChannel}
              verifiedIdentifier={verifiedIdentifier}
              profile={verifiedProfile}
              initialCouponCode={initialCouponCode}
              onBack={() => setStep('verify-identity')}
              onClose={handleClose}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CustomizePackageModal;
